/**
 * PlanOverridesContext (W25 lean override slice).
 *
 * Provides a plan's override state + actions to the degree-audit tree so each
 * requirement row can show its override status + affordance without threading
 * props through every layer. When absent (no provider), consumers render nothing
 * — so the audit is unaffected outside the override-aware mount.
 */

import React, { createContext, ReactNode, useContext } from 'react';

import { usePlanOverrides, UsePlanOverridesResult } from '../hooks/usePlanOverrides';

const PlanOverridesContext = createContext<UsePlanOverridesResult | null>(null);

interface PlanOverridesProviderProps {
  planId: number | null;
  /** Called after a create/revoke so the audit can refetch (W26 effective status). */
  onMutate?: () => void;
  children: ReactNode;
}

export function PlanOverridesProvider({ planId, onMutate, children }: PlanOverridesProviderProps) {
  const value = usePlanOverrides(planId, onMutate);
  return (
    <PlanOverridesContext.Provider value={value}>{children}</PlanOverridesContext.Provider>
  );
}

/** Returns the plan-overrides value, or null when not inside a provider. */
export function usePlanOverridesContext(): UsePlanOverridesResult | null {
  return useContext(PlanOverridesContext);
}

export default PlanOverridesContext;
